import { Association } from '../../../shared/models/association.model';
import { AssociationService } from '../../../shared/services/association.service';
import { AssociationNewComponent } from '../association-new/association-new.component';
import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataTable, DataTableTranslations, DataTableParams } from 'angular-datatables-all-versions';

import { ToastyService } from 'ng2-toasty';
import { AssociationsListDataSource } from './associations-list.datasource';
import { IPaginationAssocs, IAssoc } from './ipagination-assocs.model';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { AssociationCerfaDownloadComponent } from '../association-cerfa-download/association-cerfa-download.component';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-associations-list',
  templateUrl: './associations-list.component.html',
  styleUrls: ['./associations-list.component.css']
})
export class AssociationsListComponent implements OnInit, AfterViewInit {

  @ViewChild('search_input', { static: true }) searchInputRef: ElementRef;
  @ViewChild('search_btn', { static: true }) searchBtnRef: ElementRef;
  @ViewChild(DataTable, { static: true }) assocsTable: DataTable;

  public site_url: string;
  associations: IAssoc[];
  associationCount = 0;
  categories: any[];
  categorySelected = 'all';
  offset = 0;
  limits = [25, 50, 100];


  dataSource: AssociationsListDataSource;

  translations = <DataTableTranslations>{
    indexColumn: 'Num.',
    expandColumn: 'Informations',
    selectColumn: 'Sélectionner',
    paginationLimit: 'Max.',
    paginationText: 'Résultats: {from} - {to} / {total}',
    paginationTotalPages: '/'
  }

  constructor(private router: Router, private route: ActivatedRoute, private toastyService: ToastyService,
    private modalService: NgbModal, private service: AssociationService, private changeDetector: ChangeDetectorRef) {
    this.dataSource = new AssociationsListDataSource(service);
    this.categories = this.route.snapshot.data['categories'];
    this.site_url = environment.base_url;
  }

  ngOnInit() {
    this.dataSource.load();
    this.dataSource.pagination.subscribe(
      (data: IPaginationAssocs) => {
        if (data && data.hasOwnProperty('meta')) {
          this.associationCount = data.meta.pagination.total_objects;
          this.associations = data.assocs.map(item => {
            item['category_name'] = item.category.name;
            return item;
          });
        }
      });
    console.log('Initial load triggered', this.assocsTable);
  }

  ngAfterViewInit(): void {
    fromEvent(this.searchBtnRef.nativeElement, 'click').pipe(debounceTime(100), distinctUntilChanged(),
      tap(() => {
        this.changeDetector.detectChanges();
        this.assocsTable.offset = 1;

        this.loadAssocsPage();
      })).subscribe(() => {
        this.dataSource.pagination.subscribe(
          () => {
            this.changeDetector.detectChanges();
          });
      });
  }

  reload(_params: DataTableParams) {
    // To prevent internal error when the current page number < 1
    if (this.assocsTable.page < 1) {
      this.offset = 1;
    }

    this.loadAssocsPage(_params);
  }

  onCategorySelected(id: any) {
    this.categorySelected = id;
    this.loadAssocsPage();
  }

  cellColor(car) {
    return 'rgb(255, 255,' + (155 + Math.floor(100 - ((car.rating - 8.7) / 1.3) * 100)) + ')';
  }

  onActivateOrDeactivateAssociation(association: Association): void {
    if (association.is_active) {
      this.switchActivation(association);
    } else {
      const errors = this.fetchInvalidProperties(association, 'assoc_address', 'assoc_object', 'assoc_rep', 'category', 'description',
        'hasBannerFile', 'hasLogoFile', 'published_at', 'recognized_at', 'website');
      if (errors.length > 0) {
        let message = 'Merci de remplir les champs suviants pour activer l’association:';
        errors.forEach(err => {
          switch (err) {
            case 'assoc_address':
              message += '<br>- L’adresse de l’association est obligatoire';
              break;
            case 'assoc_object':
              message += '<br>- L’objet de l’association est obligatoire';
              break;
            case 'assoc_rep':
              message += '<br>- Les d&eacute;tails du repr&eacute;sentant de l’association est obligatoire';
              break;
            case 'category':
              message += '<br>- La cat&eacute;gorie de l’association est obligatoire';
              break;
            case 'description':
              message += '<br>- La description de l’association est obligatoire';
              break;
            case 'hasBannerFile':
              message += '<br>- L’image de couverture de l’association est obligatoire';
              break;
            case 'hasLogoFile':
              message += '<br>- Le logo de l’association est obligatoire';
              break;
            case 'published_at':
              message += '<br>- La date de publication de l’association est obligatoire';
              break;
            case 'recognized_at':
              message += '<br>- La date de reconnaissance de l’association est obligatoire';
              break;
            case 'website':
              message += '<br>- Le site web de l’association est obligatoire';
              break;
            default:
              break;
          }
        });

        this.toastyService.error({
          title: 'Attention',
          msg: message,
          showClose: true,
          timeout: 5000,
          theme: 'bootstrap',
        });
      } else {
        this.switchActivation(association);
      }
    }
  }

  onActivateOrDeactivateImpact(association: Association): void {
    if (association.is_impact) {
      this.switchActivationImpact(association);
    } else {
      if (!association.is_active) {
        let message = "L'association doit être active pour activer Students for Impact.";

        this.toastyService.error({
          title: 'Attention',
          msg: message,
          showClose: true,
          timeout: 5000,
          theme: 'bootstrap',
        });
      } else {
        this.switchActivationImpact(association);
      }
    }
  }

  export() {
    this.service.find({
      offset: 1, limit: this.associationCount, search: this.searchInputRef.nativeElement.value,
      category: this.categorySelected
    } as DataTableParams).subscribe(
      (data: IPaginationAssocs) => {
        const exportdata: any[] = [];
        data.assocs.forEach(item => {
          exportdata.push({
            'Nom': item.name,
            'Site': item.website,
            'Categorie': item.category.name,
            'Representant': item.assoc_rep.firstname + ' ' + item.assoc_rep.name,
            'Mangopay ID': item.mangopay_user_id ? item.mangopay_user_id : 'N/A',
            'Mangopay Wallet ID': item.mangopay_wallet_id ? item.mangopay_wallet_id : 'N/A',
            'Pays': item.assoc_address ? item.assoc_address.country_iso : 'N/A'
          });
        });
        const csvData = this.convertToCSV(exportdata);
        const blob = new Blob([csvData], { type: 'text/csv' });
        if (window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveBlob(blob, 'associations.csv');
        } else {
          const a = window.document.createElement('a');
          a.href = window.URL.createObjectURL(blob);
          a.download = 'associations.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      });
  }

  onCreateAssociation() {
    const modalRef = this.modalService.open(AssociationNewComponent);
    modalRef.componentInstance.associationCategories = this.categories;

    modalRef.result.then((result) => {
      console.log('result', result);
      if ('assoc-modal-success' === result) {
        this.loadAssocsPage();
      }

      this.toastyService.success({
        title: 'Success',
        msg: 'Association cr&eacute&eacute',
        showClose: true,
        timeout: 5000,
        theme: 'bootstrap',
      })
    }, err => console.log('error', err));
  }

  exportCerfa() {
    const modalRef = this.modalService.open(AssociationCerfaDownloadComponent, { backdrop: 'static' });
    modalRef.componentInstance.assocs = this.associations;
    modalRef.result.then((res) => {
      console.log('res', res);
      if (res === 'download-success') {
        this.toastyService.success({
          title: 'Success',
          msg: 'T&eacute;l&eacute;chargement fait',
          showClose: true,
          timeout: 5000,
          theme: 'bootstrap',
        })
      } else if (res === 'download-error') {
        this.toastyService.warning({
          title: 'Attention',
          msg: 'Pas de fichier pour les param&egrave;tres choisis',
          showClose: true,
          timeout: 5000,
          theme: 'bootstrap',
        })
      }
    }, (err) => {
      console.log('export cerfa err', err);
    });
  }

  private switchActivation(input) {
    this.service.activateOrDeactivate(input).subscribe(
      data => {
        this.loadAssocsPage();

        // notify user
        const verb = data.is_active ? 'activ&eacute;e' : 'desactiv&eacute;e';
        this.toastyService.success({
          title: 'Success',
          msg: 'Association ' + verb,
          showClose: true,
          timeout: 5000,
          theme: 'bootstrap',
        });
      },
      () => this.toastyService.error({
        title: 'Attention',
        msg: 'Une erreur s\'est produite, reessayer plus tard',
        showClose: true,
        timeout: 5000,
        theme: 'bootstrap',
      })
    );
  }

  private switchActivationImpact(input) {
    this.service.activateOrDeactivateImpact(input).subscribe(
      data => {
        this.loadAssocsPage();

        // notify user
        const verb = data.is_impact ? 'activ&eacute;e' : 'desactiv&eacute;e';
        this.toastyService.success({
          title: 'Success',
          msg: 'Students for impact ' + verb,
          showClose: true,
          timeout: 5000,
          theme: 'bootstrap',
        });
      },
      () => this.toastyService.error({
        title: 'Attention',
        msg: 'Une erreur s\'est produite, reessayer plus tard',
        showClose: true,
        timeout: 5000,
        theme: 'bootstrap',
      })
    );
  }

  private loadAssocsPage(sort = null) {
    this.offset = this.assocsTable.page < 1 ? 1 : this.assocsTable.page;
    this.dataSource.load(this.searchInputRef.nativeElement.value, this.offset, this.assocsTable.limit, this.categorySelected, sort = null);
  }

  private fetchInvalidProperties(input: any, ...properties: string[]): string[] {
    const res: string[] = [];
    Object.keys(input).forEach(key => {
      const value = input[key];
      if (value instanceof Object) {
        this.fetchInvalidProperties(value, key);
      } else if (properties.indexOf(key) !== -1 && (typeof value === 'undefined' || value === null)) {
        res.push(key);
      }
    });

    return res;
  }

  private convertToCSV(objArray) {
    const replacer = (_key, value) => value === null ? '' : value
    const header = Object.keys(objArray[0])
    let csv = objArray.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(';'))
    csv.unshift(header.join(';'));
    csv = csv.join('\r\n')
    return csv;
  }

  getStripeConnectUrl(associationId: string): string {
    return `${this.site_url}${environment.stripe_onboarding_url}${associationId}`;
  }

  copyStripeLink(associationId: string): void {
    const url = this.getStripeConnectUrl(associationId);

    // Copier dans le presse-papier
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
      this.toastyService.success({
        title: 'Succès',
        msg: 'Lien copié dans le presse-papier',
        showClose: true,
        timeout: 3000,
        theme: 'bootstrap',
      });
    } catch (err) {
      this.toastyService.error({
        title: 'Erreur',
        msg: 'Impossible de copier le lien',
        showClose: true,
        timeout: 3000,
        theme: 'bootstrap',
      });
    } finally {
      document.body.removeChild(textArea);
    }
  }
}
